import { useCallback, useEffect, useRef, useState } from "react";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	ICellRendererParams,
} from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";

import { StraightenerRunsToolbar } from "./StraightenerRunsToolbar";
import { StraightenerRunsSummary } from "./StraightenerRunsSummary";
import {
	useStraightenerPickers,
	useStraightenerRuns,
} from "../hooks/useStraightenerRuns";
import type { StraightenerRunRow } from "../models/StraightenerRuns";

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";

// The SP already returns Datetime converted to local time (same as legacy),
// so render the raw value without any timezone shift.
const fmtLocal = (v?: string | null): string =>
	v ? String(v).replace("T", " ").substring(0, 19) : "";

const fmtNum = (v: unknown, dec: number): string =>
	v == null || v === "" ? "" : Number(v).toFixed(dec);

const fmtPlcDimension = (v: number | null | undefined): string =>
	v == null ? "" : String(Number(v.toFixed(4)));

const productDisplay = (row?: StraightenerRunRow): string =>
	row?.ProductSize?.trim() || row?.PlcProductDescription || "";

const ProductCell = ({ data }: ICellRendererParams<StraightenerRunRow>) => {
	if (!data) return null;
	const issue = data.ProductMappingIssue;
	return (
		<Tooltip
			title={
				<Box sx={{ whiteSpace: "pre-line" }}>
					{issue ? `${issue}\n` : ""}
					{`Recorded PLC: ${data.PlcProductDescription}\nProfile code: ${data.ProfileType ?? "missing"}\nMES product ID: ${data.ProductSizeId ?? "unmatched"}`}
				</Box>
			}
			arrow
			describeChild
		>
			<Box
				component="span"
				tabIndex={issue ? 0 : undefined}
				sx={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 0.5,
					maxWidth: "100%",
					color: issue ? "error.main" : "#000",
					fontWeight: issue ? 600 : "inherit",
				}}
			>
				{issue && (
					<WarningAmberIcon
						fontSize="small"
						titleAccess="Unresolved MES product"
						sx={{ flexShrink: 0 }}
					/>
				)}
				<Box
					component="span"
					sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
				>
					{productDisplay(data)}
				</Box>
			</Box>
		</Tooltip>
	);
};

const TRACKING_COLUMNS: ColDef<StraightenerRunRow>[] = [
	{
		field: "EstimatedDurationSeconds",
		headerName: "Est. duration (s)",
		minWidth: 140,
		headerTooltip:
			"Estimated from calibrated-speed update cadence; not a measured presence duration.",
		valueFormatter: (p) => fmtNum(p.value, 1),
	},
	{
		field: "TargetBarsPerLayer",
		headerName: "Bars/layer (set)",
		minWidth: 135,
		headerTooltip:
			"PLC layer target. This is not a measured count of bars through the straightener.",
	},
	{
		field: "BarLengthFeet",
		headerName: "Bar length (ft)",
		minWidth: 130,
		valueFormatter: (p) => fmtNum(p.value, 2),
	},
	{
		field: "ExpectedDurationSeconds",
		headerName: "Expected duration (s)",
		hide: true,
		valueFormatter: (p) => fmtNum(p.value, 1),
	},
	{
		field: "CutLengthFeet",
		headerName: "Cut length (ft)",
		hide: true,
		valueFormatter: (p) => fmtNum(p.value, 2),
	},
	{ field: "SpeedSampleCount", headerName: "Speed updates", hide: true },
	{
		field: "ChainReleaseDatetime",
		headerName: "Chain release",
		hide: true,
		valueFormatter: (p) => fmtLocal(p.value),
	},
	{
		field: "FirstSpeedSampleDatetime",
		headerName: "First speed update",
		hide: true,
		valueFormatter: (p) => fmtLocal(p.value),
	},
	{
		field: "LastSpeedSampleDatetime",
		headerName: "Last speed update",
		hide: true,
		valueFormatter: (p) => fmtLocal(p.value),
	},
	{
		field: "ProductSampleAgeSeconds",
		headerName: "Product sample age (s)",
		hide: true,
		valueFormatter: (p) => fmtNum(p.value, 1),
	},
	{ field: "TrackingVersion", headerName: "Tracking version", hide: true },
];

const COLUMN_DEFS: ColDef<StraightenerRunRow>[] = [
	{
		field: "Datetime",
		headerName: "Datetime",
		flex: 1.6,
		minWidth: 160,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => fmtLocal(p.data?.Datetime),
	},
	{
		field: "ProductSize",
		headerName: "Product",
		flex: 2,
		minWidth: 220,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueGetter: (p) => productDisplay(p.data),
		cellRenderer: ProductCell,
	},
	{
		field: "PlcProductDescription",
		headerName: "PLC Product",
		hide: true,
		headerTooltip:
			"Description built from the recorded PLC width, height, thickness and profile code.",
		flex: 1.8,
		minWidth: 190,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		tooltipField: "PlcProductDescription",
	},
	{
		field: "ProductSizeId",
		headerName: "MES Product ID",
		hide: true,
	},
	{
		field: "ProductMappingIssue",
		headerName: "Product Mapping Issue",
		hide: true,
	},
	{
		field: "LastGapTime",
		headerName: "Last Gap Time",
		flex: 1.2,
		minWidth: 110,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => fmtNum(p.data?.LastGapTime, 2),
	},
	{
		field: "SetFPM",
		headerName: "Set FPM",
		flex: 1,
		minWidth: 90,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "CalibratedFPM",
		headerName: "Calibrated FPM",
		flex: 1.3,
		minWidth: 120,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => fmtNum(p.data?.CalibratedFPM, 2),
	},
	{
		field: "Width",
		headerName: "Width",
		flex: 0.9,
		minWidth: 80,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => fmtPlcDimension(p.data?.Width),
	},
	{
		field: "Height",
		headerName: "Height",
		flex: 0.9,
		minWidth: 80,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => fmtPlcDimension(p.data?.Height),
	},
	{
		field: "MaterialThickness",
		headerName: "Material Thickness",
		flex: 1.4,
		minWidth: 130,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => fmtPlcDimension(p.data?.MaterialThickness),
	},
	{
		field: "ProfileType",
		headerName: "Profile Type",
		hide: true,
		flex: 1.1,
		minWidth: 100,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	...TRACKING_COLUMNS,
];

const EXCEL_COLUMN_KEYS = [
	"Datetime",
	"ProductSize",
	"ProductSizeId",
	"PlcProductDescription",
	"ProductMappingIssue",
	"LastGapTime",
	"SetFPM",
	"CalibratedFPM",
	"Width",
	"Height",
	"MaterialThickness",
	"ProfileType",
	...TRACKING_COLUMNS.map((column) => column.field!),
];

const EXCEL_DECIMALS: Record<string, number> = {
	LastGapTime: 2,
	CalibratedFPM: 2,
	EstimatedDurationSeconds: 1,
	ExpectedDurationSeconds: 1,
	BarLengthFeet: 2,
	CutLengthFeet: 2,
	ProductSampleAgeSeconds: 1,
};

const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day").subtract(2, "days")),
	end: urlParam.date(() => moment().startOf("day")),
	productSizeId: urlParam.int(),
	productTypeId: urlParam.int(),
};

export const StraightenerRunsReport = () => {
	const [
		{ start, end, productSizeId, productTypeId },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	const [searchParams, setSearchParams] = useSearchParams();
	useEffect(() => {
		if (!searchParams.has("detection")) return;
		setSearchParams(
			(previous) => {
				const next = new URLSearchParams(previous);
				next.delete("detection");
				return next;
			},
			{ replace: true },
		);
	}, [searchParams, setSearchParams]);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<StraightenerRunRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useStraightenerRuns({
		start: startStr,
		end: endStr,
		productSizeId,
		productTypeId,
	});
	const pickersQuery = useStraightenerPickers();

	// A shared link can name a product size that is no longer offered.
	useEffect(() => {
		if (
			!pickersQuery.isSuccess ||
			productSizeId == null ||
			(productSizeId === -1 && productTypeId == null)
		)
			return;
		if (
			!pickersQuery.data.some(
				(size) =>
					size.Id === productSizeId &&
					(productTypeId == null || size.ProductTypeId === productTypeId),
			)
		)
			dropValues("productSizeId");
	}, [
		pickersQuery.isSuccess,
		pickersQuery.data,
		productSizeId,
		productTypeId,
		dropValues,
	]);

	const rows = dataQuery.data ?? [];
	const productTypes = Array.from(
		new Map(
			(pickersQuery.data ?? [])
				.filter((size) => size.ProductTypeId != null)
				.map((size) => [
					size.ProductTypeId!,
					{ Id: size.ProductTypeId!, Name: size.ProductTypeName ?? "" },
				]),
		).values(),
	);
	const productSizes = (pickersQuery.data ?? []).filter(
		(size) => productTypeId == null || size.ProductTypeId === productTypeId,
	);
	useEffect(() => {
		if (
			pickersQuery.isSuccess &&
			productTypeId != null &&
			!pickersQuery.data.some((size) => size.ProductTypeId === productTypeId)
		) {
			dropValues("productTypeId");
		}
	}, [pickersQuery.isSuccess, pickersQuery.data, productTypeId, dropValues]);

	const handleGridReady = useCallback(
		(e: GridReadyEvent<StraightenerRunRow>) => {
			gridApiRef.current = e.api;
		},
		[],
	);

	const handleExportToExcel = useCallback(() => {
		gridApiRef.current?.exportDataAsExcel({
			fileName: "Straightener_Runs.xlsx",
			columnKeys: EXCEL_COLUMN_KEYS,
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				const data = params.node?.data as StraightenerRunRow | undefined;
				if (colId === "Datetime") return fmtLocal(data?.Datetime);
				if (
					[
						"ChainReleaseDatetime",
						"FirstSpeedSampleDatetime",
						"LastSpeedSampleDatetime",
					].includes(colId)
				)
					return fmtLocal(
						data?.[
							colId as
								| "ChainReleaseDatetime"
								| "FirstSpeedSampleDatetime"
								| "LastSpeedSampleDatetime"
						],
					);
				// Export the same product label shown in the report.
				if (colId === "ProductSize") return productDisplay(data);
				if (["Width", "Height", "MaterialThickness"].includes(colId)) {
					return fmtPlcDimension(
						data?.[colId as "Width" | "Height" | "MaterialThickness"],
					);
				}
				if (colId in EXCEL_DECIMALS) {
					return fmtNum(
						data?.[colId as keyof StraightenerRunRow],
						EXCEL_DECIMALS[colId],
					);
				}
				return params.value ?? "";
			},
		});
	}, []);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}
	if (pickersQuery.error && !errorMsg) {
		setErrorMsg((pickersQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Straightener Runs
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<StraightenerRunsToolbar
					onReset={() => resetValues()}
					canReset={!isDefault()}
					start={start}
					end={end}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					productTypeId={productTypeId}
					productTypes={productTypes}
					onProductTypeChange={(id) =>
						setFilters({ productTypeId: id, productSizeId: null })
					}
					productSizeId={productSizeId}
					onProductSizeChange={(id) => setFilters({ productSizeId: id })}
					productSizes={[
						...(productTypeId == null
							? [
									{
										Id: -1,
										Name: "Unmatched (PLC)",
										ProductTypeId: null,
										ProductTypeName: null,
									},
								]
							: []),
						...productSizes,
					]}
					onExportExcel={handleExportToExcel}
				/>
			</Paper>

			<StraightenerRunsSummary count={rows.length} />

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden" }}>
				{dataQuery.isLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<AgGridReact<StraightenerRunRow>
						rowData={rows}
						columnDefs={COLUMN_DEFS}
						defaultColDef={{
							resizable: true,
							cellStyle: CENTER_CELL,
							headerClass: CENTER_HEADER,
						}}
						suppressContextMenu
						headerHeight={32}
						rowHeight={28}
						onGridReady={handleGridReady}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
